import { Transition } from '@headlessui/react'
import clsx from 'clsx'
import Button from 'components/tailwind/Button'
import Modal from 'components/tailwind/Modal'
import { Heading } from 'components/ui/Heading'
import { useState } from 'react'
import ReactMarkdown from 'react-markdown'

type FeaturesMessageProps = {
  className?: string
  ctaPrimary: {
    title: string
    onClick: () => void
  }
  ctaSecondary?: {
    title: string
    onClick: () => void
  }
  isPulsing?: boolean
}

const features = [
  // Beispiel-Video-URL (kann pro Feature angepasst werden)

  {
    title: '🧩 Teamanalyse',
    description:
      'Erkenne auf einen Blick, **wie gut ein Bewerbender dein bestehendes Team ergänzt.**',
    link: 'https://aivy.app'
  },
  {
    title: '🎯 Orientierungsmodul',
    description:
      'Zeige Talenten, **welche Stelle am besten zu ihnen passt** – auf deiner **Karriereseite** oder für **interne Mobilität.**',
    link: 'https://aivy.app'
  },
  {
    title: '🧠 Talentpool & Stärkenprofile',
    description:
      'Alle Ergebnisse **strukturiert, vergleichbar und jederzeit abrufbar.**',
    link: 'https://aivy.app'
  },
  {
    title: '🔄 Kreuzmatching',
    description:
      'Vergleiche Bewerbende miteinander oder finde **weitere passende Rollen für ein Talent.**',
    link: 'https://aivy.app'
  },
  {
    title: '🗣️ Interviewleitfaden',
    description:
      'Erhalte automatisch **passende Fragen für jedes Talent** – basierend auf den individuellen Ergebnissen.',
    link: 'https://aivy.app'
  },
  {
    title: '🔗 Integrationen',
    description:
      'Verbinde Aivy mit deinem bestehenden **ATS oder HR-System** – **schnell, reibungslos und mit nur wenigen Klicks.**',
    link: 'https://aivy.app'
  }
]

// Beispiel-Video-URL (kann pro Feature angepasst werden)
const FEATURE_VIDEO_URL = 'https://www.youtube.com/embed/dQw4w9WgXcQ'

export default function FeaturesMessage({
  className,
  ctaPrimary,
  ctaSecondary,
  isPulsing
}: FeaturesMessageProps) {
  const [ctaVisible, setCtaVisible] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalVideoUrl, setModalVideoUrl] = useState<string | null>(null)

  const handleCtaClick = (onClick: () => void) => {
    setCtaVisible(false)
    setTimeout(onClick, 300) // Animation duration
  }

  const handleLearnMore = (videoUrl: string) => {
    setModalVideoUrl(videoUrl)
    setModalOpen(true)
  }

  return (
    <div
      className={clsx(
        'w-full rounded-lg border border-lightGrey bg-lightPaleGrey p-4',
        className
      )}
    >
      <Heading level={5} className='mb-2'>
        Die wichtigsten Features im Überblick
      </Heading>
      {features.map((feature, idx) => (
        <div
          key={idx}
          className={clsx(
            'mb-4 rounded-md border border-blue-200 bg-blue-50 p-4'
          )}
        >
          <Heading level={4} className='mb-2'>
            {feature.title}
          </Heading>
          <ReactMarkdown
            components={{
              a: ({ href, children }) => (
                <a
                  href={href}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='mt-1 block font-semibold text-primaryBlue underline'
                >
                  {children}
                </a>
              ),
              h4: ({ children }) => (
                <Heading level={4} className='mb-2 mt-4'>
                  {children}
                </Heading>
              ),
              p: ({ children }) => (
                <p className='mb-3 text-balance text-sm/normal text-darkBlack'>
                  {children}
                </p>
              ),
              strong: ({ children }) => (
                <strong className='font-semibold'>{children}</strong>
              )
            }}
            className='text-sm font-normal text-darkBlack'
          >
            {feature.description}
          </ReactMarkdown>
          <button
            type='button'
            className='mt-1 inline-block rounded text-sm font-semibold text-primaryBlue underline focus:outline-none focus:ring-2 focus:ring-primaryBlue'
            onClick={() => handleLearnMore(FEATURE_VIDEO_URL)}
          >
            Mehr erfahren
          </button>
          {/* Video Modal */}
          <Modal
            size='2xl'
            open={modalOpen}
            setOpen={() => setModalOpen(false)}
          >
            {modalOpen && (
              <div className='h-96 w-full max-w-xl p-4'>
                {modalVideoUrl && (
                  <iframe
                    src={modalVideoUrl}
                    title='Feature Video'
                    frameBorder='0'
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                    className='aspect-video h-full w-full rounded-lg'
                  />
                )}
              </div>
            )}
          </Modal>
        </div>
      ))}
      <Transition
        show={ctaVisible}
        enter='transition-opacity duration-300'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='transition-opacity duration-300'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'
      >
        <div className='mt-4 flex flex-col gap-y-2'>
          {ctaPrimary && (
            <Button.PrimaryBase
              text={ctaPrimary.title}
              isPulsing={isPulsing}
              onClick={() => handleCtaClick(ctaPrimary.onClick)}
            />
          )}
          {ctaSecondary && (
            <Button.SecondaryBase
              text={ctaSecondary.title}
              onClick={() => handleCtaClick(ctaSecondary.onClick)}
            />
          )}
        </div>
      </Transition>
    </div>
  )
}
